import type { ReactNode } from "react";
import { Info } from "lucide-react";

import { cn } from "../lib/cn";
import { Tooltip } from "./Tooltip";

export interface InfoTipProps {
  /** The explanation, shown on hover and keyboard focus, and read by assistive technology. */
  children: ReactNode;
  /** Names what the tip is about, for the button's accessible name. */
  label: string;
  side?: "top" | "right" | "bottom" | "left";
  className?: string;
  /** Defaults to a compact explanatory panel; constrained to the viewport. */
  width?: number;
}

/**
 * A small "i" that holds an explanation out of the way: for settings whose
 * description would otherwise sit permanently under their label. The text is
 * also in the button (visually hidden), so it isn't lost to screen readers.
 */
export function InfoTip({ children, label, side = "top", className, width = 256 }: InfoTipProps) {
  return (
    <Tooltip
      content={
        <span
          className="block font-normal leading-5"
          style={{ width, maxWidth: "calc(100vw - 2rem)" }}
        >
          {children}
        </span>
      }
      side={side}
    >
      <button
        type="button"
        data-info-tip
        className={cn(
          "inline-grid size-5 shrink-0 place-items-center rounded-full text-muted transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus",
          className,
        )}
      >
        <Info className="size-3.5" aria-hidden />
        <span className="sr-only">
          {label}: {children}
        </span>
      </button>
    </Tooltip>
  );
}
